<script setup lang="ts">
import { mdiChevronUp } from "@mdi/js";
import Silk from "~/components/layout/TrackRacing/Shared/Silk.vue";
import { IOutcomeWithPrice } from "./Event.vue";
import OutcomeAdditionalInfo from "../Shared/Outcome/OutcomeAdditionalInfo.vue";

interface Props {
	outcome: IOutcomeWithPrice;
	isSelected: boolean | undefined;
	isPlaceSelected?: boolean | undefined;
	isPickToPlaceModal?: boolean | undefined;
	isEventActive: boolean | undefined;
	isPlaceMarketSelected?: boolean | undefined;
}
defineProps<Props>();
const emit = defineEmits(["outcome-selected"]);
const active = ref(false);

const getOrdinalSuffix = (position: number): string => {
	if (!Number.isInteger(position)) return `${position}`;

	const suffixes = ["th", "st", "nd", "rd"];
	const v = position % 100;
	return `${position}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
};
</script>

<template>
	<UiAccordion
		:activeIndex="1"
		class="overflow-hidden"
		:class="[
			{ 'bg-primary-600': isSelected && !isPlaceMarketSelected },
			{ 'border-dark-900 bg-dark-800': isSelected && isPlaceMarketSelected },
			{
				'bg-light-200 dark:bg-dark-700': (!isEventActive && !isSelected) || (isEventActive && !outcome.isTradingActive),
			},
			{ 'bg-light-50 dark:bg-dark-800': isEventActive && !isSelected && outcome.isTradingActive },
		]"
		@tab-open="active = true"
		@tab-close="active = false"
	>
		<UiAccordionTab
			:headerWrapperClass="'items-center text-xs leading-none font-bold text-dark-800 dark:text-light-50 relative  pl-9 overflow-hidden py-1'"
			:disableToggle="false"
		>
			<template #header>
				<div
					@click.stop="emit('outcome-selected', outcome)"
					:class="['ml-1 h-[30px] grid grid-cols-[40px_1fr_45px] items-center', { 'text-light-50': isSelected }]"
				>
					<Silk class="flex items-center" :isEventActive="isEventActive" :image-location="outcome.additionalInfo?.ImageLocation" />
					<div class="flex items-center text-xs mr-1 truncate">
						<div class="truncate">
							{{ outcome.displayName }}
							<div v-if="outcome.additionalInfo?.JockeyName && outcome.additionalInfo?.TrainerName" class="text-[10px] mt-1 font-light truncate">
								{{ outcome.additionalInfo?.TrainerName }}/{{ outcome.additionalInfo?.JockeyName }}
							</div>
						</div>
					</div>
					<div class="flex justify-end text-right text-xs mx-2">
						<div>
							<div v-if="outcome?.raceResult" :class="[' mb-1 font-bold', { 'text-primary-600': !isSelected }]">
								{{ getOrdinalSuffix(outcome.raceResult.position) }}
							</div>
							<span v-if="isEventActive && !outcome.isTradingActive"> NR </span>
							<span v-else>
								{{ outcome?.price?.priceDecimal.toFixed(2) }}
							</span>
						</div>
					</div>
				</div>
			</template>
			<template #header-icon>
				<div class="absolute -translate-y-1/2 left-1 top-1/2 border border-light-500 dark:border-dark-500 rounded-md p-1 mr-1">
					<UiIcon
						:class="[
							'z-10 w-[20px] h-[20px] fill-dark-500 dark:fill-light-50 transition-all pb-1 ',
							{ 'fill-light-50': isSelected },
							{ 'rotate-180': !active },
						]"
						:path="mdiChevronUp"
					/>
				</div>
			</template>
			<div class="p-1 text-xs bg-light-200 dark:bg-dark-700 border border-light-400 dark:border-dark-500 border-b-0" v-if="active">
				<OutcomeAdditionalInfo v-if="outcome.additionalInfo" :outcome-additional-info="outcome.additionalInfo" />
			</div>
		</UiAccordionTab>
	</UiAccordion>
</template>
